import { type ReactNode } from 'react';
import { type Control, Controller, type FieldValues, type Path } from 'react-hook-form';

import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldError } from '@/components/ui/field';
import { Label } from '@/components/ui/label';
// TEMPORARY: unused while promotional consent checkbox is commented out pending A2P approval
// import { links } from '@/lib/constants';
import { cn } from '@/lib/utils';
import {
  type SmsConsentFormData,
  smsConsentDefaultValues,
  smsConsentSchema,
} from '@/lib/validation-schemas';

export { smsConsentDefaultValues, smsConsentSchema };
export type { SmsConsentFormData };

type SmsConsentFieldName = keyof SmsConsentFormData;

interface Props<T extends FieldValues & SmsConsentFormData> {
  control: Control<T>;
  formId: string;
  labelClassName?: string;
}

export function SmsConsentCheckboxes<T extends FieldValues & SmsConsentFormData>({
  control,
  formId,
  labelClassName,
}: Props<T>) {
  return (
    <div className="space-y-4">
      {/* TEMPORARY: promotional consent checkbox commented out pending A2P approval */}
      {/* <ConsentCheckbox
        control={control}
        formId={formId}
        name="promotionalConsent"
        labelClassName={labelClassName}
        label={
          <>
            I agree to receive promotional text messages about Conduit Health&apos;s and its
            affiliates&apos; products and services. Message & data rates may apply. Message
            frequency varies. Reply STOP to cancel and HELP for help. See{' '}
            <a href={links.conduit_privacy_policy_url} className="font-medium underline">
              privacy policy
            </a>{' '}
            &{' '}
            <a href={links.conduit_terms_of_service_url} className="font-medium underline">
              terms of service
            </a>
            .
          </>
        }
      /> */}
      <ConsentCheckbox
        control={control}
        formId={formId}
        name="transactionalConsent"
        labelClassName={labelClassName}
        label={
          <>
            I agree to receive transactional messages (such as appointment reminders, delivery
            notifications, and customer care updates) from Conduit Health and its affiliates.
            Consent not required to obtain care. Message & data rates may apply. Reply STOP to
            cancel and HELP for help.
          </>
        }
      />
    </div>
  );
}

interface ConsentCheckboxProps<T extends FieldValues & SmsConsentFormData> {
  control: Control<T>;
  formId: string;
  name: SmsConsentFieldName;
  label: ReactNode;
  labelClassName?: string;
}

function ConsentCheckbox<T extends FieldValues & SmsConsentFormData>({
  control,
  formId,
  name,
  label,
  labelClassName,
}: ConsentCheckboxProps<T>) {
  const inputId = `${formId}-${name}`;

  return (
    <Controller
      name={name as Path<T>}
      control={control}
      render={({ field, fieldState }) => (
        <Field data-invalid={fieldState.invalid} className="gap-2">
          <div className="flex items-start gap-2">
            <Checkbox
              id={inputId}
              checked={field.value}
              onCheckedChange={(checked) => field.onChange(checked === true)}
              aria-invalid={fieldState.invalid}
              className="mt-0.5"
            />
            <Label
              htmlFor={inputId}
              className={cn('text-sm leading-5 font-normal text-charcoal-400', labelClassName)}
            >
              {label}
            </Label>
          </div>
          {fieldState.error?.message && <FieldError errors={[fieldState.error]} />}
        </Field>
      )}
    />
  );
}
